/**
 * mobile-bridge.ts
 *
 * Web → native logout bridge. When the header runs inside a native WebView
 * (the YPO mobile shell), logout is handed off to the native host over a
 * JavaScript bridge instead of running the web logout redirect.
 *
 * See mobile-logout-bridge-spec.md for the full contract.
 */

/**
 * Case-insensitive detection of the native mobile shell via User-Agent token.
 * The native host injects one of these tokens into the WebView User-Agent:
 *   - iOS:     `iOSConnect`
 *   - Android: `AndroidConnect`
 *
 * `userAgent` is injectable so the check is unit-testable without stubbing
 * `navigator`.
 */
export function isMobileAppBrowser(userAgent: string = navigator.userAgent): boolean {
    const ua = userAgent.toLowerCase()
    return ua.includes('iosconnect') || ua.includes('androidconnect')
}

/**
 * Signals logout to the native host over whichever bridge object it exposes.
 * The bridge is chosen by feature-detection (which object exists), not by the
 * UA token. The `logout` signal carries no payload.
 *
 * Returns `true` if a bridge was found and invoked, `false` if no bridge is
 * present — letting the caller fall back to the normal web logout.
 */
export function sendNativeLogout(): boolean {
    // Android-style: injected object with direct methods.
    if (window.appInterface?.logout) {
        window.appInterface.logout()
        return true
    }

    // iOS-style (WKWebView): named message handlers, empty-string payload.
    // Guard the specific `logout` handler — it may not be registered even when
    // `messageHandlers` exists.
    if (window.webkit?.messageHandlers?.logout) {
        window.webkit.messageHandlers.logout.postMessage('')
        return true
    }

    return false
}
